import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useEffect, useState } from 'preact/hooks';
import { CampaignAnalyticsTab } from '@/components/campaigns/tabs/CampaignAnalyticsTab';
import { CampaignWebsTab } from '@/components/campaigns/tabs/CampaignWebsTab';
import { CampaignDetailsTab } from '@/components/campaigns/tabs/CampaignDetailsTab';
import { CampaignCalendarTab } from '@/components/campaigns/tabs/CampaignCalendarTab';
import { MeetingsTab } from '@/components/common/MeetingsTab';
import { useLocation } from 'preact-iso';
import { AudienceTab } from '@/components/campaigns/tabs/AudienceTab';
import { CampaignLeadsTab } from '@/components/campaigns/CampaignLeadsTab';
import { CampaignSettingsTab } from '@/components/campaigns/tabs/CampaignSettingsTab';
import { KnowledgeBasePanel } from '@/components/knowledgeBase/KnowledgeBasePanel';
import { KnowledgeBaseLevel } from '@/types/knowledgeBase';
export function CampaignTabbedContent({ campaign, audienceAppend, clientCompanyId, selectedWorkspaceId, onCampaignUpdated }) {
    const location = useLocation();
    // Parse tab from a URL string; returns null if invalid or absent.
    const parseTabFromUrl = (urlStr) => {
        try {
            const url = new URL(urlStr, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
            const t = url.searchParams.get('tab');
            if (t === 'webs' || t === 'leads' || t === 'audience' || t === 'calendar' || t === 'meetings' || t === 'analytics' || t === 'details' || t === 'knowledge-base' || t === 'settings')
                return t;
            return null;
        }
        catch {
            return null;
        }
    };
    // Initialize activeTab from current location (including deep links) to avoid mounting 'webs' by default.
    const [activeTab, setActiveTab] = useState(() => {
        const initial = parseTabFromUrl(location?.url || (typeof window !== 'undefined' ? window.location.href : '/'));
        return initial || 'webs';
    });
    // Newly created audience file (from modal) for optimistic append
    // Holds a newly created audience file for optimistic render on Audience tab.
    const [newAudienceFile] = useState(null);
    // Initialize from URL and react to browser navigation
    useEffect(() => {
        const t = parseTabFromUrl(location?.url || '/');
        if (t && t !== activeTab)
            setActiveTab(t);
    }, [location?.url]);
    const tabs = [
        { key: 'webs', label: 'Webs' },
        { key: 'leads', label: 'Leads' },
        { key: 'audience', label: 'Audience' },
        { key: 'calendar', label: 'Calendar' },
        { key: 'meetings', label: 'Meetings' },
        { key: 'analytics', label: 'Analytics' },
        { key: 'details', label: 'Details' },
        { key: 'knowledge-base', label: 'Knowledge Base' },
        { key: 'settings', label: 'Settings' },
    ];
    return (_jsxs("div", { className: "mt-2", children: [_jsx("div", { className: "border-b border-slate-200 dark:border-slate-700", children: _jsx("nav", { className: "-mb-px flex gap-6", "aria-label": "Tabs", children: tabs.map(t => {
                        const isActive = activeTab === t.key;
                        return (_jsx("button", { onClick: () => {
                                if (t.key === activeTab)
                                    return;
                                setActiveTab(t.key);
                                // Update query param ?tab=<key> while preserving other params
                                try {
                                    const url = new URL(location?.url || '/', window.location.origin);
                                    url.searchParams.set('tab', t.key);
                                    location.route(url.pathname + url.search + url.hash);
                                }
                                catch {
                                    // no-op
                                }
                            }, className: `whitespace-nowrap py-3 text-sm font-medium border-b-2 transition-colors ` +
                                (isActive
                                    ? 'border-primary-600 text-primary-700 dark:text-primary-300'
                                    : 'border-transparent text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:border-slate-300 dark:hover:border-slate-500'), children: t.label }, t.key));
                    }) }) }), _jsxs("div", { className: "pt-6", children: [activeTab === 'webs' && (_jsx(CampaignWebsTab, { campaign: campaign, active: true })), activeTab === 'leads' && (_jsx(CampaignLeadsTab, { clientCompanyId: clientCompanyId, workspaceId: campaign.WorkspaceId, campaignId: campaign.Id })), activeTab === 'audience' && (_jsx(AudienceTab, { campaign: campaign, audienceAppend: audienceAppend || newAudienceFile })), activeTab === 'calendar' && (_jsx(CampaignCalendarTab, { campaign: campaign, clientCompanyId: clientCompanyId })), activeTab === 'meetings' && (_jsx(MeetingsTab, { campaignId: campaign.Id, workspaceId: campaign.WorkspaceId })), activeTab === 'analytics' && (_jsx(CampaignAnalyticsTab, { campaign: campaign })), activeTab === 'details' && (_jsx(CampaignDetailsTab, { campaign: campaign, clientCompanyId: clientCompanyId, selectedWorkspaceId: selectedWorkspaceId, onCampaignUpdated: onCampaignUpdated })), activeTab === 'knowledge-base' && (_jsx(KnowledgeBasePanel, { clientCompanyId: clientCompanyId, workspaceId: selectedWorkspaceId, campaignId: campaign.Id, level: KnowledgeBaseLevel.Campaign })), activeTab === 'settings' && (_jsx(CampaignSettingsTab, { campaignId: campaign.Id, clientCompanyId: clientCompanyId, workspaceId: selectedWorkspaceId }))] })] }));
}
